import type { BoxProps, PopoverProps } from '@shopify/polaris';
import type { GSelectOption } from './type';
import type { ReactElement } from 'react';

import { BlockStack, Box, Icon, InlineStack, Popover, Text } from '@shopify/polaris';
import { GBlockStack, GButton, GDiv, GTooltip } from '~/presentation/ui';
import { GActivatorPopover } from '../GActivatorPopover';
import { CheckIcon } from '@shopify/polaris-icons';
import { useToggle } from '~/hooks/useToggle';
import { GClickable } from '../GClickable';
import { useState } from 'react';
import { cls } from '~/utils';

interface GSelectProps {
  placeholder: string;
  activator?: ReactElement;
  activatorBoxProps?: BoxProps;
  options: GSelectOption[];
  selected: string;
  disabled?: boolean;
  showCheckIcon?: boolean;
  boxProps?: BoxProps;
  popoverProps?: Pick<PopoverProps, 'preferredAlignment' | 'fullWidth'>;
  onSelect: (value: string) => void;
  onOpen?: () => void;
  onClose?: () => void;
}

export const GSelect = ({
  placeholder,
  activator,
  activatorBoxProps,
  options,
  selected,
  disabled = false,
  showCheckIcon = false,
  boxProps,
  popoverProps,
  onSelect,
  onOpen,
  onClose,
}: GSelectProps) => {
  const [hoverItem, setHoverItem] = useState<string | null>(null);
  const { value: active, toggle: toggleActive } = useToggle(false);

  const selectedOption = options.find((o) => o.value === selected);
  const buttonText = selectedOption?.label ?? selected;
  const buttonTooltip = selectedOption?.tooltip ?? '';

  const handleOpen = () => {
    toggleActive();
    onOpen?.();
  };

  const handleClose = () => {
    toggleActive();
    onClose?.();
  };

  return (
    <Popover
      active={active}
      activator={
        activator ? (
          <GClickable onClick={handleOpen}>{activator}</GClickable>
        ) : (
          <div>
            <GTooltip content={!active ? buttonTooltip : undefined} hoverDelay={500}>
              <GActivatorPopover
                onClick={handleOpen}
                active={active}
                content={buttonText || placeholder}
                textProps={{ tone: !selected ? 'disabled' : undefined }}
                disabled={disabled}
                boxProps={activatorBoxProps}
              />
            </GTooltip>
          </div>
        )
      }
      preferredAlignment="left"
      {...popoverProps}
      onClose={handleClose}
    >
      <Box padding="150" minWidth="200px" {...boxProps}>
        <BlockStack gap="100">
          {options.map((option) => {
            const isSelected = selected === option.value;
            const isDisabled = option.disabled;
            const isHasSecondaryAction = !!option.secondaryAction;
            const isHover = hoverItem === option.value;

            const bgSelected = isSelected ? 'bg-surface-selected' : undefined;
            const bgHover = isHover ? 'bg-surface-hover' : undefined;
            const bgDisabled = isDisabled ? 'bg-surface' : undefined;
            const bgDefault = 'bg-surface';
            const background = bgDisabled || bgSelected || bgHover || bgDefault;

            return (
              <Box
                key={option.value}
                paddingBlock={isHasSecondaryAction ? '050' : '150'}
                paddingInline="150"
                borderRadius="200"
                background={background}
                overflowX="hidden"
                {...(isHasSecondaryAction && { paddingInlineEnd: '050' })}
              >
                <GClickable
                  fullWidth
                  onClick={() => {
                    if (option.disabled) return;

                    onSelect(option.value);
                    handleClose();
                  }}
                  cursor={isDisabled ? 'default' : 'pointer'}
                  onMouseEnter={() => setHoverItem(option.value)}
                  onMouseLeave={() => setHoverItem(null)}
                >
                  <InlineStack align="space-between" blockAlign="center" gap="150" wrap={false}>
                    <GTooltip content={option.tooltip ?? undefined}>
                      <Box overflowX="hidden">
                        <GBlockStack
                          align="center"
                          {...(isHasSecondaryAction && { minHeight: '28px' })}
                        >
                          <InlineStack gap="150" blockAlign="center" wrap={false}>
                            {option.prefix}

                            <Text
                              as="p"
                              variant="bodyMd"
                              fontWeight={isSelected ? 'semibold' : 'regular'}
                              truncate
                              tone={isDisabled ? 'disabled' : undefined}
                            >
                              {option.title ?? option.label}
                            </Text>

                            {option.suffix}
                          </InlineStack>

                          {option.description && (
                            <Text as="p" variant="bodyMd" tone="subdued">
                              {option.description}
                            </Text>
                          )}
                        </GBlockStack>
                      </Box>
                    </GTooltip>

                    {option.secondaryAction && (
                      <GDiv
                        className={cls(
                          'flex items-center min-w-7 h-7 justify-center overflow-hidden ',
                          {
                            'opacity-0': !isHover,
                          },
                        )}
                        onClick={(e) => {
                          e?.stopPropagation();
                        }}
                      >
                        <GButton
                          inset
                          tooltip={option.secondaryAction.tooltip ?? undefined}
                          variant={option.secondaryAction.variant ?? 'tertiary'}
                          icon={option.secondaryAction.icon}
                          onClick={() => option.secondaryAction?.onClick?.()}
                          disabled={option.secondaryAction.disabled}
                        >
                          {option.secondaryAction.children}
                        </GButton>
                      </GDiv>
                    )}

                    {showCheckIcon && isSelected && (
                      <span>
                        <Icon source={CheckIcon} />
                      </span>
                    )}
                  </InlineStack>
                </GClickable>
              </Box>
            );
          })}
        </BlockStack>
      </Box>
    </Popover>
  );
};
